import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors";
const CORR_ESTADOS = ["abierta", "asignada", "en_progreso", "corregida", "verificada", "cerrada"];

export default function AuditTab({ plan, onChange }) {
  const [queue, setQueue] = useState(null);
  const [audits, setAudits] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [form, setForm] = useState(null); // {wu, resultado, descripcion}

  const load = useCallback(() => {
    api.get(`/planning/plans/${plan.id}/audit-queue`).then((r) => setQueue(r.data)).catch(() => setQueue([]));
    api.get(`/planning/plans/${plan.id}/audits`).then((r) => setAudits(r.data)).catch(() => {});
    api.get(`/planning/plans/${plan.id}/corrections`).then((r) => setCorrections(r.data)).catch(() => {});
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const submitAudit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/planning/plans/${plan.id}/audits`, {
        work_unit_id: form.wu.wu_id, resultado: form.resultado, descripcion: form.descripcion,
      });
      toast.success(form.resultado === "conforme" ? "Unidad auditada y cerrada" : "Auditoría registrada — orden de corrección creada");
      setForm(null); load(); onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  const updCorrection = async (c, estado) => {
    try { await api.patch(`/planning/corrections/${c.id}`, { estado }); load(); onChange(); }
    catch { toast.error("Error"); }
  };

  if (queue === null) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="audit-tab">
      <div className="space-y-4">
        <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3">
          <div className="overline text-navy/40">Cola de auditoría ({queue.length})</div>
          {queue.length === 0 ? (
            <p className="text-sm text-navy/40">Ninguna unidad lista para auditar. Una unidad entra a la cola cuando todas sus actividades técnicas están completadas.</p>
          ) : queue.map((wu) => (
            <div key={wu.wu_id} className="rounded-lg border border-navy/10 p-3.5 flex flex-wrap items-center gap-3" data-testid={`audit-queue-${wu.label}`}>
              <div>
                <div className="font-medium text-sm">{wu.label}</div>
                <div className="text-[11px] text-navy/40">
                  {Object.entries(wu.values || {}).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  {wu.completado ? ` · completada ${wu.completado}` : ""} · {(wu.recursos || []).join(", ")}
                </div>
              </div>
              <button onClick={() => setForm({ wu, resultado: "conforme", descripcion: "" })} data-testid={`audit-btn-${wu.label}`}
                className="ml-auto rounded-full bg-navy text-cream text-xs px-4 py-2 hover:bg-navy-soft inline-flex items-center gap-1.5">
                <ClipboardCheck className="w-3.5 h-3.5" /> Auditar
              </button>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-2">
          <div className="overline text-navy/40">Auditorías registradas ({audits.length})</div>
          {audits.slice(0, 15).map((a) => (
            <div key={a.id} className="text-sm flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.resultado === "conforme" ? "bg-emerald-50 text-emerald-700" : a.resultado === "critico" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{a.resultado}</span>
              <span className="text-navy/60 text-xs">{a.auditor} · {(a.created_at || "").slice(0, 10)}</span>
              {a.descripcion && <span className="text-navy/40 text-xs truncate">{a.descripcion}</span>}
            </div>
          ))}
          {audits.length === 0 && <p className="text-sm text-navy/40">Sin auditorías aún.</p>}
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3">
        <div className="overline text-navy/40">Órdenes de corrección ({corrections.length})</div>
        {corrections.length === 0 ? (
          <p className="text-sm text-navy/40">Sin correcciones. Se generan automáticamente cuando una auditoría resulta observada o crítica.</p>
        ) : corrections.map((c) => (
          <div key={c.id} className="rounded-lg border border-navy/10 p-3.5 space-y-1.5" data-testid={`correction-${c.id}`}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{c.descripcion}</span>
              <select className="ml-auto text-xs border border-navy/10 rounded-lg px-2 py-1 bg-white outline-none" value={c.estado}
                onChange={(e) => updCorrection(c, e.target.value)} data-testid={`correction-estado-${c.id}`}>
                {CORR_ESTADOS.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
              </select>
            </div>
            <div className="text-[11px] text-navy/40">{(c.created_at || "").slice(0, 10)}{c.responsable ? ` · ${c.responsable}` : ""}{c.fecha_limite ? ` · límite ${c.fecha_limite}` : ""}</div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-[80] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submitAudit} className="bg-cream rounded-lg w-full max-w-md p-6 space-y-4" data-testid="audit-form">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Auditar {form.wu.label}</h3>
              <button type="button" onClick={() => setForm(null)} className="text-navy/40 hover:text-navy">✕</button>
            </div>
            <div className="flex gap-2">
              {[["conforme", "Conforme", "border-emerald-300 bg-emerald-50 text-emerald-700"],
                ["observado", "Observado", "border-amber-300 bg-amber-50 text-amber-700"],
                ["critico", "Crítico", "border-red-300 bg-red-50 text-red-700"]].map(([v, l, cls]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, resultado: v })} data-testid={`audit-result-${v}`}
                  className={`flex-1 rounded-lg border py-2.5 text-sm transition-all ${form.resultado === v ? cls + " font-medium" : "border-navy/10 bg-white text-navy/50"}`}>{l}</button>
              ))}
            </div>
            <textarea className={inputCls} rows={3} placeholder={form.resultado === "conforme" ? "Comentarios (opcional)" : "Describe la observación (generará una orden de corrección)"}
              value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} data-testid="audit-descripcion" />
            <button data-testid="audit-submit"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> Registrar auditoría
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
